import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { ProgressRingTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getDynamicPropSignal } from '../helpers'

const RADIUS = 16
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const SIZE_PX: Record<string, number> = {
  sm: 40,
  md: 56,
  lg: 72,
}

@Component({
  selector: 'flx-progress-ring-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './progress-ring-wrapper.component.html',
  styleUrl: './progress-ring-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxProgressRingWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<ProgressRingTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  value: Signal<string>

  readonly max = computed(() => this.flowxProps()?.max ?? 100)
  readonly showLabel = computed(() => this.flowxProps()?.showLabel ?? true)

  readonly percent = computed(() => {
    const max = this.max()
    const value = Number(this.value()) || 0
    return max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  })

  readonly label = computed(() => `${Math.round(this.percent())}%`)

  readonly sizePx = computed(() => SIZE_PX[this.flowxProps()?.size || 'md'])

  readonly circumference = CIRCUMFERENCE

  readonly dashOffset = computed(() => CIRCUMFERENCE * (1 - this.percent() / 100))

  readonly sizeClass = computed(() => `progress-ring--${this.flowxProps()?.size || 'md'}`)
  readonly variantClass = computed(
    () => `progress-ring--${this.flowxProps()?.variant || 'primary'}`
  )
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.sizeClass(), this.variantClass()]
      .filter(Boolean)
      .join(' ')
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      this.value = getDynamicPropSignal(String(props.value ?? ''), templateConfig)
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
